import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  CheckCircle, 
  XCircle, 
  Settings, 
  Eye,
  UserPlus,
  Shield,
  BarChart3,
  Trash2,
  Check,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function PlatformAdmin({ currentUser }) {
  const [pharmacies, setPharmacies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userPharmacies, setUserPharmacies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [stats, setStats] = useState({});

  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    phone: '',
    pharmacy_id: '',
    role: 'user'
  });

  const headers = { 'X-User-Id': currentUser?.id || '' };

  useEffect(() => {
    fetchPlatformData();
  }, []);

  const fetchPlatformData = async () => {
    setLoading(true);
    try {
      const [pharmaciesRes, usersRes, userPharmaciesRes] = await Promise.all([
        axios.get(`${API}/pharmacies`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/user-pharmacies`, { headers })
      ]);

      setPharmacies(pharmaciesRes.data);
      setAllUsers(usersRes.data);
      setUserPharmacies(userPharmaciesRes.data || []);

      // Calculate stats
      const activePharmacies = pharmaciesRes.data.filter(p => p.status === 'active').length;
      const totalUsers = usersRes.data.length;
      
      setStats({
        totalPharmacies: pharmaciesRes.data.length,
        activePharmacies,
        totalUsers,
        pendingPharmacies: pharmaciesRes.data.filter(p => p.status === 'pending').length
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (pharmacyId, newStatus) => {
    try {
      await axios.patch(
        `${API}/pharmacies/${pharmacyId}/status`,
        { status: newStatus },
        { headers }
      );
      toast.success(`Statut mis à jour : ${newStatus}`);
      fetchPlatformData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeletePharmacy = async (pharmacy) => {
    const usersCount = getPharmacyUsers(pharmacy.id).length;
    
    const confirmMessage = `⚠️ ATTENTION : SUPPRESSION DÉFINITIVE ⚠️\n\nVous êtes sur le point de SUPPRIMER la pharmacie "${pharmacy.name}".\n\nCette action IRRÉVERSIBLE supprimera :\n• La pharmacie\n• ${usersCount} utilisateur(s) associé(s)\n• Toutes les données (revenus, dépenses, clôtures, etc.)\n• Toutes les relations user-pharmacy\n\nTapez le nom de la pharmacie pour confirmer :\n"${pharmacy.name}"`;
    
    const userInput = window.prompt(confirmMessage);
    
    if (userInput !== pharmacy.name) {
      if (userInput !== null) {
        toast.error('Nom incorrect - Suppression annulée');
      }
      return;
    }

    try {
      await axios.delete(`${API}/pharmacies/${pharmacy.id}`, { headers });
      toast.success(`Pharmacie "${pharmacy.name}" supprimée avec succès`);
      fetchPlatformData();
    } catch (error) {
      console.error('Error deleting pharmacy:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUserData, { headers });
      
      // Create user-pharmacy relationship
      await axios.post(`${API}/user-pharmacies`, {
        user_id: newUserData.username, // Will be replaced by actual user_id
        pharmacy_id: newUserData.pharmacy_id,
        role: newUserData.role
      }, { headers });

      toast.success('Utilisateur créé avec succès !');
      setNewUserDialog(false);
      setNewUserData({
        username: '',
        password: '',
        email: '',
        full_name: '',
        phone: '',
        pharmacy_id: '',
        role: 'user'
      });
      fetchPlatformData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const getPharmacyUsers = (pharmacyId) => {
    // Get all user-pharmacy relationships for this pharmacy
    const userPharmaciesForPharmacy = userPharmacies.filter(up => up.pharmacy_id === pharmacyId);
    
    // Map to users with their role from user_pharmacies
    return userPharmaciesForPharmacy.map(up => {
      const user = allUsers.find(u => u.id === up.user_id);
      if (!user) return null;
      
      return {
        ...user,
        role: up.role, // Role from user_pharmacies
        permissions: up.permissions
      };
    }).filter(Boolean); // Remove nulls
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Administration Plateforme</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Gérez toutes les pharmacies et utilisateurs</p>
        </div>
        <Badge className="bg-purple-600 text-white px-4 py-2 text-sm">
          <Shield className="w-4 h-4 mr-2" />
          Super Admin
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Pharmacies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.totalPharmacies || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Pharmacies Actives</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.activePharmacies || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">En Attente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{stats.pendingPharmacies || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{stats.totalUsers || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pharmacies" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pharmacies">
            <Building2 className="w-4 h-4 mr-2" />
            Pharmacies
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Utilisateurs
          </TabsTrigger>
        </TabsList>

        {/* Pharmacies Tab */}
        <TabsContent value="pharmacies">
          <Card>
            <CardHeader>
              <CardTitle>Toutes les Pharmacies</CardTitle>
              <CardDescription>Gérez les pharmacies de la plateforme</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pharmacie</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Adresse</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Utilisateurs</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pharmacies.map((pharmacy) => (
                      <TableRow key={pharmacy.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                              {pharmacy.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium">{pharmacy.name}</p>
                              <p className="text-xs text-gray-500">{pharmacy.registration_number}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{pharmacy.phone}</TableCell>
                        <TableCell className="text-sm text-gray-600">{pharmacy.address}</TableCell>
                        <TableCell>
                          {pharmacy.status === 'active' ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-700">
                              <XCircle className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getPharmacyUsers(pharmacy.id).length} utilisateurs</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {pharmacy.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handleStatusChange(pharmacy.id, 'active')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Activer
                              </Button>
                            )}
                            {pharmacy.status === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStatusChange(pharmacy.id, 'suspended')}
                                className="text-red-600 border-red-600"
                              >
                                Suspendre
                              </Button>
                            )}
                            {pharmacy.status === 'suspended' && (
                              <Button
                                size="sm"
                                onClick={() => handleStatusChange(pharmacy.id, 'active')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Réactiver
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedPharmacy(pharmacy);
                                setDialogOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeletePharmacy(pharmacy)}
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Tous les Utilisateurs</CardTitle>
                <CardDescription>Gérez les utilisateurs de toutes les pharmacies</CardDescription>
              </div>
              <Dialog open={newUserDialog} onOpenChange={setNewUserDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-green-600 hover:bg-green-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Nouvel Utilisateur
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Créer un Utilisateur</DialogTitle>
                    <DialogDescription>Ajoutez un nouvel utilisateur à une pharmacie</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label>Nom Complet *</Label>
                      <Input
                        value={newUserData.full_name}
                        onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Username *</Label>
                      <Input
                        value={newUserData.username}
                        onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        value={newUserData.password}
                        onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newUserData.email}
                        onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Pharmacie *</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={newUserData.pharmacy_id}
                        onChange={(e) => setNewUserData({...newUserData, pharmacy_id: e.target.value})}
                        required
                      >
                        <option value="">Sélectionner une pharmacie</option>
                        {pharmacies.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Rôle *</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={newUserData.role}
                        onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                      >
                        <option value="user">Utilisateur</option>
                        <option value="admin">Admin</option>
                        <option value="caissier">Caissier</option>
                      </select>
                    </div>
                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                      Créer l'Utilisateur
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Pharmacie(s)</TableHead>
                    <TableHead>Rôle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.full_name || user.username}</p>
                          <p className="text-xs text-gray-500">@{user.username}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{user.email || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.pharmacies?.map(p => (
                            <Badge key={p.pharmacy_id} variant="outline" className="text-xs">
                              {pharmacies.find(ph => ph.id === p.pharmacy_id)?.name || p.pharmacy_id}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="capitalize">{user.role || 'user'}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pharmacy Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détails de la Pharmacie</DialogTitle>
          </DialogHeader>
          {selectedPharmacy && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Nom</Label>
                  <p className="font-medium">{selectedPharmacy.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Registre</Label>
                  <p className="font-medium">{selectedPharmacy.registration_number}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <p className="font-medium">{selectedPharmacy.email}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Téléphone</Label>
                  <p className="font-medium">{selectedPharmacy.phone}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-600">Adresse</Label>
                  <p className="font-medium">{selectedPharmacy.address}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-600 mb-2 block">Utilisateurs ({getPharmacyUsers(selectedPharmacy.id).length})</Label>
                <div className="space-y-2">
                  {getPharmacyUsers(selectedPharmacy.id).map(user => (
                    <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-sm">{user.full_name || user.username}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      <Badge>{user.role || 'user'}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PlatformAdmin;
